import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, dateFr, CSTATUS } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { SignOut, FilePdf, Scales, CheckCircle, Coins, Signature } from "@phosphor-icons/react";

export default function Portal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [statement, setStatement] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [signing, setSigning] = useState(null);
  const [signName, setSignName] = useState("");
  const [accepted, setAccepted] = useState(false);

  const loadContracts = () => api.get("/portal/contracts").then((r) => setContracts(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/portal/statement").then((r) => setStatement(r.data)).catch(() => {});
    api.get("/portal/invoices").then((r) => setInvoices(r.data)).catch(() => {});
    loadContracts();
  }, []);

  const token = () => localStorage.getItem("token");
  const viewPdf = (inv) => window.open(`${API}/invoices/${inv.id}/pdf?auth=${token()}`, "_blank");
  const viewContractPdf = (c) => window.open(`${API}/contracts/${c.id}/pdf?auth=${token()}`, "_blank");
  const openSign = (c) => { setSignName(c.client_rep || user?.name || ""); setAccepted(false); setSigning(c); };
  const sign = async () => {
    try {
      const { data } = await api.post(`/portal/contracts/${signing.id}/sign`, { name: signName, accepted });
      toast.success(data.status === "active" ? "Contrat signé et activé. Merci !" : "Contrat signé. Merci !");
      setSigning(null); loadContracts();
    } catch (e) { toast.error(apiError(e)); }
  };

  const cards = statement ? [
    { label: "Total facturé", value: money(statement.total_billed), icon: Coins, accent: "text-foreground" },
    { label: "Total payé", value: money(statement.total_paid), icon: CheckCircle, accent: "text-emerald-700" },
    { label: "Solde dû", value: money(statement.balance), icon: Scales, accent: statement.balance > 0 ? "text-red-600" : "text-emerald-700" },
  ] : [];
  const toSign = contracts.filter((c) => c.status === "sent" && !c.signatures?.client?.signed_at);

  return (
    <div className="min-h-screen bg-background text-left">
      <header className="border-b border-border bg-card sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/innova-fact-dark.png" alt="innova-fact" className="h-8 w-auto" />
            <div className="border-l border-border pl-3">
              <div className="font-heading font-bold leading-none">{statement?.company?.business_name || "Portail client"}</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Espace client</div>
            </div>
          </div>
          <Button data-testid="portal-logout" variant="outline" size="sm" className="rounded-sm" onClick={async () => { await logout(); navigate("/login"); }}>
            <SignOut size={16} className="mr-2" /> Déconnexion
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-8 space-y-8" data-testid="portal-page">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tighter">Bonjour, {user?.name}</h1>
          <p className="text-muted-foreground mt-1">Consultez vos factures, vos contrats et votre état de compte.</p>
        </div>

        {toSign.length > 0 && (
          <div data-testid="portal-sign-banner" className="border border-primary/40 bg-primary/5 rounded-sm p-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3"><Signature size={22} className="text-primary" /><div><div className="font-medium">{toSign.length} contrat{toSign.length > 1 ? "s" : ""} en attente de votre signature</div><div className="text-sm text-muted-foreground">Consultez le PDF puis signez électroniquement ci-dessous.</div></div></div>
            <Button data-testid="portal-sign-first-btn" className="rounded-sm" onClick={() => openSign(toSign[0])}>Signer maintenant</Button>
          </div>
        )}

        <div className="grid sm:grid-cols-3 gap-4">
          {cards.map((c) => (
            <div key={c.label} className="border border-border bg-card p-5 rounded-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{c.label}</span>
                <c.icon size={18} className={c.accent} />
              </div>
              <div className={`mt-3 font-heading text-2xl font-bold tabular-nums ${c.accent}`}>{c.value}</div>
            </div>
          ))}
        </div>

        {contracts.length > 0 && (
          <div className="border border-border bg-card rounded-sm overflow-x-auto" data-testid="portal-contracts">
            <div className="px-6 py-4 border-b border-border"><h2 className="font-heading font-bold">Mes contrats de service</h2></div>
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Numéro</TableHead>
                  <TableHead>Durée</TableHead>
                  <TableHead>Début → Fin</TableHead>
                  <TableHead className="text-right">Mensuel (avant taxes)</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contracts.map((c) => {
                  const st = CSTATUS[c.status] || CSTATUS.draft;
                  const signed = !!c.signatures?.client?.signed_at;
                  return (
                    <TableRow key={c.id} data-testid={`portal-contract-${c.id}`}>
                      <TableCell className="font-mono font-medium">{c.contract_number}</TableCell>
                      <TableCell>{c.term_months} mois</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{dateFr(c.start_date)} → {dateFr(c.end_date)}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{money(c.monthly_total)}</TableCell>
                      <TableCell><span className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-medium ${st.cls}`}>{signed && c.status === "sent" ? "Signé — en attente" : st.label}</span></TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button data-testid={`portal-contract-pdf-${c.id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={() => viewContractPdf(c)}><FilePdf size={18} /></Button>
                          {c.signed_file && <Button data-testid={`portal-contract-signed-${c.id}`} variant="ghost" size="sm" className="h-8 rounded-sm text-xs" onClick={() => window.open(`${API}/contracts/${c.id}/signed-file?auth=${token()}`, "_blank")}>Copie signée</Button>}
                          {c.status === "sent" && !signed && <Button data-testid={`portal-contract-sign-${c.id}`} size="sm" className="rounded-sm" onClick={() => openSign(c)}><Signature size={16} className="mr-1" /> Signer</Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="border border-border bg-card rounded-sm overflow-x-auto">
          <div className="px-6 py-4 border-b border-border"><h2 className="font-heading font-bold">Mes factures</h2></div>
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Numéro</TableHead>
                <TableHead>Émission</TableHead>
                <TableHead>Échéance</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">PDF</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.filter((i) => i.status !== "draft").length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">Aucune facture disponible.</TableCell></TableRow>
              )}
              {invoices.filter((i) => i.status !== "draft").map((inv) => (
                <TableRow key={inv.id} data-testid={`portal-invoice-${inv.id}`}>
                  <TableCell className="font-mono font-medium">{inv.invoice_number}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{dateFr(inv.issue_date)}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{dateFr(inv.due_date)}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{money(inv.total)}</TableCell>
                  <TableCell><StatusBadge status={inv.status} /></TableCell>
                  <TableCell className="text-right">
                    <Button data-testid={`portal-pdf-${inv.id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={() => viewPdf(inv)}><FilePdf size={18} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>

      <Dialog open={!!signing} onOpenChange={(v) => !v && setSigning(null)}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Signature size={20} /> Signer le contrat {signing?.contract_number}</DialogTitle>
            <DialogDescription>
              Frais mensuels récurrents de <strong>{money(signing?.monthly_total)}</strong> (avant taxes), durée minimale de {signing?.term_months} mois.
              En inscrivant votre nom, vous apposez une signature électronique horodatée ayant la même valeur qu'une signature manuscrite.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => signing && viewContractPdf(signing)}><FilePdf size={16} className="mr-2" /> Lire le contrat (PDF)</Button>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Votre nom complet (signature)</Label>
              <Input data-testid="portal-sign-name" className="rounded-sm font-heading text-lg italic" value={signName} onChange={(e) => setSignName(e.target.value)} />
            </div>
            <label className="flex items-start gap-3 text-sm cursor-pointer">
              <Checkbox data-testid="portal-sign-accept" checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} className="mt-0.5" />
              <span>J'ai lu le contrat, je le comprends et j'accepte d'être lié par ses modalités et conditions, incluant les limites de responsabilité.</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setSigning(null)}>Annuler</Button>
            <Button data-testid="portal-sign-confirm" className="rounded-sm" disabled={!accepted || signName.trim().length < 2} onClick={sign}>Signer le contrat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
